import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './ProductDetails.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { fetchProductDetails } from '../../redux/actions/ProductDetailsAction';
import { addToCart, addToCartByVariants, fetchCartItems } from '../../redux/actions/cartAction';
import { toast } from 'react-toastify';
import { addComment } from '../../redux/actions/commentAction';
import { clearCommentState } from '../../redux/slices/commentSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../App';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { toggleFavorite, fetchFavorites } from '../../redux/actions/favoriteAction';


// دالة إنشاء session ID فريد
const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// دالة الحصول على session ID من sessionStorage أو إنشاء واحد جديد
const getSessionId = () => {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
        sessionId = generateSessionId();
        sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
};

function ProductDetails() {
    const { isDarkMode } = useDarkMode();
    const [quantity, setQuantity] = useState(0);
    const [variantQuantities, setVariantQuantities] = useState({});
    const [selectedVariants, setSelectedVariants] = useState([]); // متغيرات مختارة متعددة
    // //console.log({log:selectedVariant});
    
    const navigate = useNavigate();
    const { t, i18n } = useTranslation("global");
    const { id } = useParams();
    const dispatch = useDispatch();
    const { product, isLoading, error } = useSelector((state) => state.productDetails);
    const user = JSON.parse(localStorage.getItem("userTools"));
    const { isLoading: commentLoading, error: commentError, success: commentSuccess } = useSelector((state) => state.comments);
    const [commentText, setCommentText] = useState("");
    const [rating, setRating] = useState(0);

    const { items: favoriteItems = [] } = useSelector((state) => state.favorite) || {};

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(fetchProductDetails({ id: id, lang: i18n.language }));
        
        // جلب المفضلة إذا كان المستخدم مسجل دخول
        const token = localStorage.getItem("token") || user?.token;
        if (token && token !== 'social_login_placeholder') {
            dispatch(fetchFavorites(i18n.language));
        }
        
        // إنشاء session ID عند فتح الموقع
        getSessionId();
    }, [dispatch, id, i18n.language]);

    // تحديث القيمة الابتدائية للكمية بناءً على count
    useEffect(() => {
        setQuantity(product?.count || 1);
    }, [product?.id, product?.count]);

    // تحديث القيم الابتدائية للمتغيرات بناءً على count لكل متغير
    useEffect(() => {
        if (product?.variants && product.variants.length > 0) {
            const initialQuantities = {};
            product.variants.forEach(variant => {
                initialQuantities[variant.id] = product.count || 1;
            });
            setVariantQuantities(initialQuantities);
            
            // لا نحتاج لاختيار أول متغير تلقائياً في النظام الجديد
        }
    }, [product?.variants]);

    const handleQuantityChange = useCallback((change) => {
        const step = product?.count || 1;
        const newQuantity = quantity + change;
        if (newQuantity >= step) {
            setQuantity(newQuantity);
        }
    }, [quantity, product]);



    // Memoized function لتحديد/إلغاء تحديد متغير متعدد
    const handleMultiVariantSelection = useCallback((variant) => {
        setSelectedVariants(prev => {
            const isSelected = prev.some(v => v.id === variant.id);
            if (isSelected) {
                // إزالة المتغير من القائمة
                return prev.filter(v => v.id !== variant.id);
            } else {
                // إضافة المتغير للقائمة مع الكمية الافتراضية
                const newVariant = {
                    ...variant,
                    quantity: product?.count || 1
                };
                return [...prev, newVariant];
            }
        });
    }, [product]);

    // Memoized function لتحديث كمية متغير محدد
    const handleMultiVariantQuantityChange = useCallback((variantId, change) => {
        const step = product?.count || 1;
        setSelectedVariants(prev => 
            prev.map(variant => {
                if (variant.id === variantId) {
                    const newQuantity = variant.quantity + change;
                    return {
                        ...variant,
                        quantity: Math.max(step, newQuantity)
                    };
                }
                return variant;
            })
        );
    }, [product]);

    const handleAddToCart = async () => {
        if (!product) {
            toast(i18n.language === 'ar' ? "المنتج غير متوفر" : "Product not available");
            return;
        }

        // Check if user is logged in
        if (!user || !user.token) {
            toast.info(t('cart.loginRequired'));
            return;
        }

        if (product?.variants && product.variants.length > 0) {
            toast(i18n.language === 'ar' ? 
                'يرجى اختيار المتغيرات المتاحة' : 
                'Please select available variants');
            return;
        }

        try {
            const result = await dispatch(addToCart({
                product_id: product.id,
                qty: quantity,
                language: i18n.language
            })).unwrap();

            dispatch(fetchCartItems(i18n.language));
            
            toast.success(i18n.language === 'ar' ? 
                `تم إضافة ${quantity} قطعة للسلة بنجاح` : 
                `Successfully added ${quantity} pieces to cart`, {
                autoClose: 1500
            });
        } catch (error) {
            toast(error.message || (i18n.language === 'ar' ? 
                'حدث خطأ أثناء إضافة المنتج للسلة' : 
                'Error adding product to cart'));
        }
    };


    const handleAddMultipleVariantsToCart = async () => {
        // Check if user is logged in
        if (!user || !user.token) {
            toast.info(t('cart.loginRequired'));
            return;
        }

        if (selectedVariants.length === 0) {
            toast(i18n.language === 'ar' ? 
                'يرجى اختيار متغير واحد على الأقل' : 
                'Please select at least one variant');
            return;
        }

        try {
            const variantsData = selectedVariants.map(variant => ({
                product_variant_id: variant.id,
                qty: variant.quantity
            }));

            await dispatch(addToCartByVariants({
                variants: variantsData,
                language: i18n.language
            })).unwrap();

            dispatch(fetchCartItems(i18n.language));
            
            toast.success(i18n.language === 'ar' ? 
                `تم إضافة ${selectedVariants.length} متغير للسلة بنجاح` : 
                `Successfully added ${selectedVariants.length} variants to cart`, {
                autoClose: 1500
            });
            
            setSelectedVariants([]);
        } catch (error) {
            toast(error.message || (i18n.language === 'ar' ? 
                'حدث خطأ أثناء إضافة المتغيرات للسلة' : 
                'Error adding variants to cart'));
        }
    };


    // إرسال التعليق
    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!user || !user.token) {
            toast(t('rating.loginRequired'));
            return;
        }
        if (!commentText.trim()) {
            toast(t('productDetails.commentRequired') || 'يرجى كتابة تعليق');
            return;
        }
        if (rating === 0) {
            toast(t('productDetails.ratingRequired') || 'يرجى اختيار تقييم');
            return;
        }
        try {
            await dispatch(addComment({ product_id: id, comment: commentText, rating })).unwrap();
            toast.success(t('productDetails.commentSuccess') || 'تم إضافة التعليق بنجاح', {
                autoClose: 1500,
                // onClose: () => window.location.reload()
            });
            setCommentText("");
            setRating(0);
            dispatch(clearCommentState());
        } catch (err) {
            toast(err?.message || t('productDetails.commentError') || 'حدث خطأ أثناء إضافة التعليق');
        }
    };
    // تبديل حالة المفضلة
    const handleToggleFavorite = useCallback(async () => {
        const token = localStorage.getItem("token") || user?.token;
        const isSocial = token === 'social_login_placeholder';
        
        if (!token || isSocial) {
            toast.info(t('home.loginRequired') || (i18n.language === 'ar' ? "يرجى تسجيل الدخول أولاً" : "Please login first"));
            return;
        }

        try {
            const res = await dispatch(toggleFavorite({ productId: id, language: i18n.language })).unwrap();
            toast.success(res.message || t('favorites.updated') || (i18n.language === 'ar' ? "تم تحديث المفضلة" : "Favorites updated"));
            // تحديث القائمة بعد التغيير
            dispatch(fetchFavorites(i18n.language));
        } catch (err) {
            toast.error(err || t('favorites.error') || (i18n.language === 'ar' ? "حدث خطأ في تحديث المفضلة" : "Error updating favorites"));
        }
    }, [dispatch, id, i18n.language, t, user?.token]);

    const isProductFavorite = useMemo(() => {
        return favoriteItems?.some(item => String(item.id) === String(id));
    }, [favoriteItems, id]);

    if (isLoading) return <div className="text-center my-5">{t("loading")}</div>;
    if (error) return <div className="text-center my-5 text-danger">{error}</div>;
    if (!product) return null;

    return (
        <div className={`product-details-container ${isDarkMode ? 'dark-mode' : ''}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} style={{ 
            background: isDarkMode ? '#0a0e27' : 'transparent',
            minHeight: '100vh'
        }}>


            <div className={`breadcrumb-box text-center py-5 mb-4 rounded ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                background: isDarkMode ? "linear-gradient(135deg, #1a1b3a 0%, #0a0e27 100%)" : "#000c3d0D",
                border: isDarkMode ? "1px solid #2d3748" : "none"
            }}>
                <h4 className="fw-bold" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>
                    {t("productDetails.title")}
                </h4>
                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                    {t("productDetails.breadcrumb")}
                </p>
            </div>

            <div className="container">
                <div className="row  gap-4">
                    <div className="col-lg-5 mb-5">
                        <div className="product-image-swiper" style={{ height: '400px', borderRadius: '10px', overflow: 'hidden' }}>
                            <Swiper
                                modules={[Navigation, Pagination]}
                                navigation
                                pagination={{ clickable: true }}
                                spaceBetween={0}
                                slidesPerView={1}
                                loop={true}
                                className="h-100"
                            >
                                {(product?.images && product.images.length > 0 ? product.images : ['/Frame.png']).map((img, index) => (
                                    <SwiperSlide key={index} className="d-flex align-items-center justify-content-center">
                                        <img
                                            src={img}
                                            alt={`Product ${index + 1}`}
                                            className="img-fluid h-100 w-100 object-fit-cover img-dark-mode"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = '/Frame.png';
                                            }}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className={`product-info ${isDarkMode ? 'dark-mode' : ''}`}>
                            <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
                                <h1 className="product-title fw-bold m-0" style={{ 
                                    fontSize: '20px', 
                                    color: isDarkMode ? 'var(--color-text)' : '#333', 
                                    lineHeight: "1.8",
                                    flex: 1
                                }}>{product?.name}</h1>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    className={`favorite-details-btn ${isProductFavorite ? 'active' : ''}`}
                                    onClick={handleToggleFavorite}
                                    style={{
                                        background: isProductFavorite ? 'rgba(255, 62, 62, 0.1)' : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                                        color: isProductFavorite ? '#ff3e3e' : (isDarkMode ? 'var(--color-muted)' : '#888'),
                                        border: 'none',
                                        width: '45px',
                                        height: '45px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '22px',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isProductFavorite ? <FaHeart /> : <FaRegHeart />}
                                </motion.button>
                            </div>
                            <p className="product-dimensions mb-3" style={{ 
                                fontSize: '18px', 
                                color: isDarkMode ? 'var(--color-muted)' : '#666' 
                            }}>{product?.category}{product?.subcategory ? ` / ${product.subcategory}` : ''}</p>
                            <div className="product-status mb-4 mt-3 d-flex align-items-center justify-content-between product-new-price">
                                <span style={{ 
                                    color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                    fontSize: "16px", 
                                    fontWeight: "bold" 
                                }}>
                                    {(product?.offer_percent > 0 || (product?.price > product?.final_price)) ? (
                                        <>
                                            <span style={{ 
                                                textDecoration: 'line-through', 
                                                color: isDarkMode ? 'var(--color-muted)' : '#888', 
                                                marginLeft: 8 
                                            }}>{product?.price} {t("cart.currency")}</span>
                                            <span style={{ 
                                                color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                fontWeight: 'bold', 
                                                marginLeft: 8 
                                            }}>{product?.final_price} {t("cart.currency")}</span>
                                            <span className="ms-2 badge bg-success">
                                                {product?.offer_percent > 0 
                                                    ? `${product?.offer_percent}%` 
                                                    : `${product?.offer || (product?.price - product?.final_price)} ${t("cart.currency")}`
                                                } {t("productDetails.discount")}
                                            </span>
                                        </>
                                    ) : (
                                        <>{product?.price} {t("cart.currency")}</>
                                    )}
                                </span>
                                {product?.status && <span className="badge bg-info text-dark mx-2">{product.status}</span>}
                            </div>
                            <div className="product-status mb-4 mt-3 d-flex align-items-center justify-content-between">
                                <span className="ms-3" style={{ 
                                    color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                    fontSize: "16px" 
                                }}>{t("productDetails.includingVAT")}</span>
                                <span className="ms-3 fw-bold" style={{ 
                                    color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                    fontSize: "18px" 
                                }}>{product?.company}</span>
                            </div>
                            <hr style={{ borderColor: isDarkMode ? 'var(--color-border)' : undefined }} />

                            {/* عرض المتغيرات إذا كانت موجودة */}
                            {product?.variants && product.variants.length > 0 ? (
                                <div className={`variants-section mb-4 ${isDarkMode ? 'dark-mode' : ''}`}>
                                    <h4 className="fw-bold mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d' }}>
                                        {t("productDetails.selectVariant")}
                                    </h4>
                                    
                                    {/* Checkbox للمتغيرات المتعددة */}
                                    <div className="variants-checkbox-list mb-4">
                                        {product.variants.map((variant) => {
                                            const isSelected = selectedVariants.some(v => v.id === variant.id);
                                            return (
                                                <div key={variant.id} 
                                                    className={`variant-checkbox-card mb-3 p-4 border rounded ${isDarkMode ? 'dark-mode' : ''}`} 
                                                    style={{
                                                        background: isSelected ? 
                                                            (isDarkMode ? 'linear-gradient(135deg, #1a237e 0%, #0a0e27 100%)' : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)') : 
                                                            (isDarkMode ? 'var(--color-card)' : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'),
                                                        border: isSelected ? 
                                                            (isDarkMode ? '2px solid var(--color-primary)' : '2px solid #000c3d') : 
                                                            (isDarkMode ? '1px solid var(--color-border)' : '1px solid rgba(0,12,61,0.1)'),
                                                        borderRadius: '15px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: isSelected ? 
                                                            (isDarkMode ? '0 4px 15px rgba(26, 35, 126, 0.3)' : '0 4px 15px rgba(0,12,61,0.2)') : 
                                                            (isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)'),
                                                        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)'
                                                    }}
                                                    onClick={() => handleMultiVariantSelection(variant)}
                                                >
                                                    <div className="form-check d-flex align-items-center">
                                                        <div className="checkbox-custom me-3" style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '6px',
                                                            border: isSelected ? 
                                                                (isDarkMode ? '3px solid var(--color-primary)' : '3px solid #000c3d') : 
                                                                (isDarkMode ? '2px solid var(--color-border)' : '2px solid #ccc'),
                                                            background: isSelected ? 
                                                                (isDarkMode ? 'var(--color-primary)' : '#000c3d') : 
                                                                (isDarkMode ? 'var(--color-card)' : 'white'),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            {isSelected && (
                                                                <i className="fas fa-check text-white" style={{ fontSize: '12px' }}></i>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div className="row align-items-center mx-2">
                                                                <div className="col-md-8">
                                                                    <label 
                                                                        className="form-check-label fw-bold d-block" 
                                                                        htmlFor={`variant-${variant.id}`}
                                                                        style={{ 
                                                                            color: isDarkMode ? 'var(--color-text)' : '#000c3d',
                                                                            fontSize: '1.2rem',
                                                                            cursor: 'pointer',
                                                                            marginBottom: '8px'
                                                                        }}
                                                                    >
                                                                        {variant.name}
                                                                    </label>
                                                                    <div className="price-info d-flex align-items-center gap-3">
                                                                        <span className="fw-bold" style={{ 
                                                                            color: isDarkMode ? 'var(--color-text)' : '#000c3d',
                                                                            fontSize: '1.1rem'
                                                                        }}>
                                                                            {variant.final_price} {t("cart.currency")}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* عرض المتغيرات المختارة مع إمكانية تعديل الكميات */}
                                    {selectedVariants.length > 0 && (
                                        <div className={`selected-variants-section mb-4 p-4 border rounded ${isDarkMode ? 'dark-mode' : ''}`} style={{
                                            background: isDarkMode ? 'var(--color-card)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            border: isDarkMode ? '2px solid var(--color-border)' : '2px solid #000c3d',
                                            borderRadius: '15px',
                                            boxShadow: isDarkMode ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,12,61,0.1)'
                                        }}>
                                            <h5 className="fw-bold mb-3" style={{ 
                                                color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                fontSize: '1.3rem' 
                                            }}>
                                                {t("productDetails.selectedVariants")} ({selectedVariants.length})
                                            </h5>
                                            
                                            {selectedVariants.map((variant) => (
                                                <div key={variant.id} className="selected-variant-item mb-3 p-3 border rounded" style={{
                                                    background: isDarkMode ? 'var(--color-bg)' : 'white',
                                                    border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #dee2e6',
                                                    borderRadius: '10px'
                                                }}>
                                                    <div className="row align-items-center">
                                                        <div className="col-md-6">
                                                            <h6 className="fw-bold mb-1" style={{ 
                                                                color: isDarkMode ? 'var(--color-text)' : '#000c3d' 
                                                            }}>
                                                                {variant.name}
                                                            </h6>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="badge px-2 py-1" style={{
                                                                    background: 'linear-gradient(135deg, #000c3d 0%, #1a237e 100%)',
                                                                    color: 'white',
                                                                    fontSize: '0.8rem'
                                                                }}>
                                                                    {variant.final_price} {t("cart.currency")}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="quantity-selector d-flex align-items-center justify-content-end gap-2">
                                                                <motion.button 
                                                                    whileHover={{ scale: 1.1 }}
                                                                    whileTap={{ scale: 0.9 }}
                                                                    className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                    onClick={() => handleMultiVariantQuantityChange(variant.id, -(product?.count || 1))}
                                                                    disabled={variant.quantity <= (product?.count || 1)}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        borderRadius: '50%',
                                                                        border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #ddd',
                                                                        background: variant.quantity <= 1 ? 
                                                                            (isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa') : 
                                                                            (isDarkMode ? 'var(--color-primary)' : '#000c3d'),
                                                                        color: '#fff',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                >
                                                                    <i className="fas fa-minus small"></i>
                                                                </motion.button>
                                                                <div
                                                                    className="text-center d-flex align-items-center justify-content-center"
                                                                    style={{ 
                                                                        minWidth: '60px', 
                                                                        width: 'fit-content',
                                                                        padding: '0 10px',
                                                                        height: '32px',
                                                                        fontSize: '1rem',
                                                                        fontWeight: 'bold',
                                                                        border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #ddd',
                                                                        borderRadius: '8px',
                                                                        background: isDarkMode ? 'var(--color-bg)' : 'white',
                                                                        color: isDarkMode ? 'var(--color-text)' : '#333'
                                                                    }}
                                                                >
                                                                    {variant.quantity}
                                                                </div>
                                                                <motion.button 
                                                                    whileHover={{ scale: 1.1 }}
                                                                    whileTap={{ scale: 0.9 }}
                                                                    className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                    onClick={() => handleMultiVariantQuantityChange(variant.id, (product?.count || 1))}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        borderRadius: '50%',
                                                                        border: 'none',
                                                                        background: isDarkMode ? 'var(--color-primary)' : '#000c3d',
                                                                        color: '#fff',
                                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                                                    }}
                                                                >
                                                                    <i className="fas fa-plus small"></i>
                                                                </motion.button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    
                                    {/* زر إضافة المتغيرات المتعددة للسلة */}
                                    <button 
                                        className="btn btn-primary py-4 px-5 d-flex align-items-center gap-3 justify-content-center w-100 mb-3" 
                                        style={{ 
                                            background: selectedVariants.length > 0 ? 
                                                'linear-gradient(135deg, #000c3d 0%, #1a237e 100%)' : 
                                                'linear-gradient(135deg, #ccc 0%, #999 100%)',
                                            border: 'none', 
                                            borderRadius: "15px",
                                            opacity: selectedVariants.length > 0 ? 1 : 0.6,
                                            boxShadow: selectedVariants.length > 0 ? 
                                                '0 4px 15px rgba(0,12,61,0.3)' : 
                                                '0 2px 8px rgba(0,0,0,0.1)',
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                            transition: 'all 0.3s ease',
                                            transform: selectedVariants.length > 0 ? 'translateY(0)' : 'translateY(2px)'
                                        }}
                                        onClick={handleAddMultipleVariantsToCart}
                                        disabled={selectedVariants.length === 0}
                                    >
                                        <i className="fas fa-shopping-cart" style={{ fontSize: '1.2rem' }}></i>
                                        <span>
                                            {selectedVariants.length > 0 ? 
                                                `${t("productDetails.addSelectedVariants")} (${selectedVariants.length})` : 
                                                t("productDetails.selectVariantsFirst")
                                            }
                                        </span>
                                        {selectedVariants.length > 0 && (
                                            <span className="badge bg-light text-dark px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                                {selectedVariants.reduce((total, variant) => total + variant.quantity, 0)} {t("productDetails.pieces")}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                /* عرض المنتج الأساسي إذا لم تكن هناك متغيرات */
                                <div className={`main-product-section mb-4 ${isDarkMode ? 'dark-mode' : ''}`}>
                                    <h4 className="fw-bold mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d' }}>
                                        {t("productDetails.requiredQuantity")}
                                    </h4>
                                    
                                    {/* Quantity Selector للمنتج الأساسي */}
                                    <div className="quantity-selector d-flex align-items-center justify-content-center gap-4 mb-4">
                                        <motion.button 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            className="quantity-btn d-flex align-items-center justify-content-center"
                                            onClick={() => handleQuantityChange(-(product?.count || 1))}
                                            disabled={quantity <= (product?.count || 1)}
                                            style={{ 
                                                width: '50px', 
                                                height: '50px', 
                                                borderRadius: '15px', 
                                                border: 'none',
                                                background: quantity <= (product?.count || 1) ? 
                                                    (isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa') : 
                                                    'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                                color: quantity <= (product?.count || 1) ? 
                                                    (isDarkMode ? 'var(--color-muted)' : '#ccc') : 
                                                    '#000c3d',
                                                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                                fontSize: '20px'
                                            }}
                                        >
                                            <i className="fas fa-minus"></i>
                                        </motion.button>
                                        <div className="quantity-display text-center" style={{ minWidth: '100px' }}>
                                            <span style={{ 
                                                fontSize: '2rem', 
                                                fontWeight: '800',
                                                color: isDarkMode ? 'var(--color-text)' : '#000c3d'
                                            }}>
                                                {quantity}
                                            </span>
                                            <p className="text-muted small m-0">{t("productDetails.pieces")}</p>
                                        </div>
                                        <motion.button 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            className="quantity-btn d-flex align-items-center justify-content-center"
                                            onClick={() => handleQuantityChange(product?.count || 1)}
                                            style={{ 
                                                width: '50px', 
                                                height: '50px', 
                                                borderRadius: '15px', 
                                                border: 'none',
                                                background: 'linear-gradient(135deg, #000c3d 0%, #1a237e 100%)',
                                                color: 'white',
                                                boxShadow: '0 8px 20px rgba(0,12,61,0.2)',
                                                fontSize: '20px'
                                            }}
                                        >
                                            <i className="fas fa-plus"></i>
                                        </motion.button>
                                    </div>
                                    

                                
                                    {/* زر إضافة المنتج الأساسي للسلة */}
                                <motion.button 
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="btn btn-primary py-3 px-4 d-flex align-items-center gap-3 justify-content-center w-100 mb-3" 
                                    style={{ 
                                        background: 'linear-gradient(135deg, #ff3e3e 0%, #ff1a1a 100%)', 
                                        border: 'none', 
                                        borderRadius: "15px",
                                        fontSize: '1.2rem',
                                        fontWeight: '700',
                                        boxShadow: '0 8px 25px rgba(255, 62, 62, 0.3)'
                                    }}
                                    onClick={handleAddToCart}
                                >
                                    <i className="fas fa-cart-plus"></i>
                                    <span>{t("cart.addToCart")}</span>
                                </motion.button>
                            </div>
                            )}

                        
                      
                        </div>
                    </div>
                </div>
                <div className={`product-description mb-5 my-5 ${isDarkMode ? 'dark-mode' : ''}`}>
                    <h3 className="section-title" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>
                        {t("productDetails.details", i18n.language === 'ar' ? "تفاصيل المنتج" : "Product Details")}
                    </h3>
                    <p style={{
                        lineHeight: '2.1',
                        color: isDarkMode ? 'var(--color-text)' : undefined
                    }}>{product?.description}</p>
                </div>

                <div className={`brand-info mb-5 ${isDarkMode ? 'dark-mode' : ''}`}>
                    <h3 className="section-title" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>
                        {t("productDetails.aboutBrand", i18n.language === 'ar' ? "عن الشركة" : "About the Brand")}
                    </h3>
                    <p style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>
                        {product?.company_desc}
                    </p>
                </div>

             
              
                
               

            </div>
        </div>
    );
}

export default ProductDetails;